﻿<template>
    <div class="mx-auto px-4 py-6 flex flex-col items-center gap-8">

        <div v-if="!isAgent_Login() && show_redirect_msg && !show_payment_methods" class="flex flex-col items-center gap-3 text-center">
            <svg class="w-10 h-10 text-pink-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
            </svg>
            <h2 class="text-lg font-semibold text-gray-800">Taking you to our secure payment portal…</h2>
            <p class="text-sm text-gray-500">You'll be redirected automatically. Please don't close this page.</p>
        </div>

        <div v-if="!isAgent_Login() && show_fallback_button && show_redirect_msg && !show_payment_methods" class="w-full flex flex-col items-center gap-2">
            <p class="text-sm text-gray-500">Redirect didn't open automatically?</p>
            <button type="button" class="button button-pink-book full" v-on:click="redirect_to_paymentprovider()">
                Make payment now
            </button>
        </div>


		<div v-if="show_payment_methods" class="w-full flex flex-col items-center gap-2">
			<div class="w-full max-w-lg mx-auto">
				<p class="text-md font-semibold uppercase tracking-wider text-gray-500 mb-3">
					Select Payment Method
				</p>
				<div class="flex flex-col gap-3 w-full">
					<button 
						v-for="category in supportedProviderMethods" 
						:key="category.profile_name" 
						type="button" 
						:class="[
							'w-full min-w-0 flex flex-col p-4 border rounded-xl transition-all duration-200 text-left focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1',
							selected_payment_provider === category.provider_name 
								? 'border-indigo-600 ring-1 ring-indigo-600 bg-indigo-50/40 shadow-sm' 
								: 'border-gray hover:border-dimgrey bg-white'
						]" 
						@click="set_payment_provider_and_checkout(category.provider_name, category.profile_id)">
						<div class="flex flex-row items-center justify-between w-full">
							<div class="flex items-center gap-3.5">
								<div :class="[
										'w-5 h-5 rounded-full border flex items-center justify-center shrink-0 transition-all duration-200',
										selected_payment_provider === category.provider_name ? 'border-indigo-600 bg-indigo-600' : 'border-gray-300 bg-white'
									]">
									<div :class="[
											'w-2 h-2 rounded-full bg-white transition-opacity duration-200',
											selected_payment_provider === category.provider_name ? 'opacity-100' : 'opacity-0'
										]"/>
								</div>

								<div>
									<span class="block text-sm font-bold text-gray-900 leading-snug">
										{{ category.title }}
									</span>
									<span class="block text-xs text-gray-500">
										{{ category.subtitle }}
									</span>
								</div>
							</div>

							<div class="h-8 w-24 flex items-center justify-end shrink-0">
								<span 
									v-if="payment_logos.find(p => (category.showaccepts == true && p.id == category.provider_name?.toLowerCase()) || category.showaccepts == false && p.id == category.categoryid?.toLowerCase())" 
									v-html="payment_logos.find(p => (category.showaccepts == true && p.id == category.provider_name?.toLowerCase()) || category.showaccepts == false && p.id == category.categoryid?.toLowerCase())?.svg"
									:alt="category.title" 
									class="max-h-12 max-w-12 object-contain" 
									style="max-width: 3rem;">
								</span>
							</div>
						</div>

						<div 
							v-if="category.showaccepts == true && category.accepts.length > 0" 
							class="flex items-center gap-1.5 mt-3 pt-2.5 border-t border-gray-100/80 w-full" 
							style="border-color: lightgrey;">
							<span class="text-[11px] text-gray-400 font-medium mr-1">Accepts:</span>
							<span 
								v-for="badge in category.accepts" 
								:key="badge" 
								class="inline-flex items-center justify-center px-2 py-1 bg-gray-50 border border-gray-200/70 rounded shadow-2xs" 
								style="border-color: lightgrey;" 
								:title="badge">
								<span 
									v-if="show_logos && payment_logos.find(p => p.id === badge.toLowerCase())" 
									v-html="payment_logos.find(p => p.id === badge.toLowerCase())?.svg" 
									class="flex items-center justify-center h-5 w-auto"
								></span>
								<span v-if="!show_logos || payment_logos.find(p => p.id === badge.toLowerCase()) == null" class="text-[10px] font-semibold tracking-wide text-gray-600">{{ payment_logos.find(p => p.id === badge.toLowerCase())?.name ?? badge }}</span>
							</span>
						</div>

						<div 
							v-if="category.provider_name === 'paypal' && selected_payment_provider === category.provider_name && !hide_payment_paypal" 
							class="w-full max-w-full min-w-0 mt-3 pt-3 border-t border-gray-200/60 overflow-hidden" 
							@click.stop>
							<div id="paypal-button-container" class="mt-2"/>
							<p id="paypal-result-message" class="text-xs text-red-500 mt-2"/>
						</div>
					</button>
				</div>
			</div>
		</div>



    <div v-if="show_hints" class="w-full border border-pink-200 rounded-lg bg-pink-50 divide-y divide-pink-200 text-sm text-gray-700">
            <div class="flex gap-3 px-4 py-3">
                <svg class="w-4 h-4 mt-0.5 shrink-0 text-pink-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
                </svg>
                <p class="m-0 p-0">Please ensure your <strong>address matches the details with your bank or credit card company</strong>. A mismatch could result in a decline.</p>
            </div>
            <div class="flex gap-3 px-4 py-3">
                <svg class="w-4 h-4 mt-0.5 shrink-0 text-pink-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
                </svg>
                <p class="m-0 p-0">You may receive an <strong>authentication message from your card issuer</strong>. If your card doesn't participate, you'll be returned here to complete your order.</p>
            </div>
        </div>

        <div v-if="!initialised_payment && !show_payment_methods" class="w-full">
            <div class="flex flex-col items-center justify-center p-6 bg-white rounded-lg space-y-3 select-none">
                <svg class="animate-spin h-12 w-12 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
            </div>
        </div>

        <div :hidden="hide_payment_ttcgateway" class="w-full h-full shadow-lg shadow-black rounded-lg border border-black border-solid p-2" :id="credit_card_div" style="background-color: #ede8da; border-color: lightgrey;"></div>
        <div :hidden="hide_payment_paymentprovider_button" class="w-full" :id="paymentprovider_div_button">
            <button type="button" class="button button-pink-book full" v-on:click="redirect_to_paymentprovider()">
                Make payment now
            </button>
        </div>
        <div :hidden="hide_payment_paymentprovider_iframe" class="w-full h-full shadow-lg shadow-black rounded-lg border border-black border-solid p-2" :id="paymentprovider_div_frame" style="background-color: #F5F5F5; border-color: lightgrey; height:800px;">
            <iframe style="height:100%; width:100%;" v-on:load="onLoadIframe" name="paymentprovider_iframe" :src="paymentiframe_url"></iframe>
        </div>

        <div v-if="!finalised_payment" class="w-full">
            <button type="button" disabled class="button button-pink-book full">
                <svg class="animate-spin h-5 w-5 mr-3 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 512 512" stroke="currentColor">
                    <path fill="currentColor" d="M440.65 12.57l4 82.77A247.16 247.16 0 0 0 255.83 8C134.73 8 33.91 94.92 12.29 209.82A12 12 0 0 0 24.09 224h49.05a12 12 0 0 0 11.67-9.26 175.91 175.91 0 0 1 317-56.94l-101.46-4.86a12 12 0 0 0-12.57 12v47.41a12 12 0 0 0 12 12H500a12 12 0 0 0 12-12V12a12 12 0 0 0-12-12h-47.37a12 12 0 0 0-11.98 12.57zM255.83 432a175.61 175.61 0 0 1-146-77.8l101.8 4.87a12 12 0 0 0 12.57-12v-47.4a12 12 0 0 0-12-12H12a12 12 0 0 0-12 12V500a12 12 0 0 0 12 12h47.35a12 12 0 0 0 12-12.6l-4.15-82.57A247.17 247.17 0 0 0 255.83 504c121.11 0 221.93-86.92 243.55-201.82a12 12 0 0 0-11.8-14.18h-49.05a12 12 0 0 0-11.67 9.26A175.86 175.86 0 0 1 255.83 432z"/>
                </svg>
                Finalising Payment
            </button>
            <p class="text-sm text-gray-500">You'll be redirected automatically. Please don't close this page.</p>
        </div>
    </div>
</template>

<script lang="ts" src="./payment_credit_card.ts"></script>